import { memo, useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import styled, { ThemeContext } from 'styled-components';
import {
  ArrowForwardIcon,
  AssociatedCarIcon,
  AssociatedLandlineIcon,
  AssociatedMarkerIcon,
  AssociatedPhoneIcon,
  AssociatedSMSIcon,
  AssociatedSmartphoneIcon,
  AssociatedVOIPIcon,
  CallReceivedIcon,
  CallSharedIcon,
  CarIcon,
  MagnifyingGlassIcon,
  PhoneIcon,
  SMSIcon,
  SMSReceivedIcon,
  SMSSharedIcon,
  LandlineIcon,
  SmartphoneIcon,
  VOIPIcon,
  MarkerIcon,
} from '@rsos/assets/icons';
import { Absolute, Box } from '@rsos/components/capstone/base';
import {
  CALL_REROUTED,
  RTI_DOWNSTREAM_SHARE,
} from '@rsos/constants/collaborator';
import {
  VOIP,
  PHASE,
  TEXT,
  ALI_UNDERSCORE,
} from '@rsos/constants/locationTypes';
import { QUEUE_FIXED_WIDTH } from '@rsos/constants/queueWidths';
import { useBooleanFlagValue } from '@rsos/feature-flags';
import { formatDownstreamAgencyNames } from '@rsos/reduxDataStore/src/features/emergencyCalls/helpers';
import { getCollaboratorTypeDisplayName } from './helpers';

/** ALI lives on the secondary list, or on a primary record whose application is ali_. */
const hasAliData = core => {
  const locations = core?.locations || [];
  const secondaryLocations = core?.secondaryLocations || [];
  return (
    secondaryLocations.length > 0 ||
    locations.some(location =>
      location?.application?.startsWith(ALI_UNDERSCORE),
    )
  );
};

// Queue width minus twice the icon's 22px left inset (EmergencyCall row
// padding), so the tooltip's edges sit equally inset within the queue.
const QUEUE_ROW_LEFT_INSET = 22;
const RTI_TOOLTIP_WIDTH = `${QUEUE_FIXED_WIDTH - QUEUE_ROW_LEFT_INSET * 2}px`;

// Width lives on the Absolute wrapper (this bubble just fills it at 100%)
// because the wrapper's containing block is the icon's own tiny box.
const RtiTooltipBubble = styled(Box).attrs({
  padding: '4px',
  backgroundColor: 'primaryText',
  color: 'background',
})`
  border-radius: 5px;
  font-size: 12px;
  word-break: break-word;
  width: 100%;
`;

export const EmergencyCallIcons = props => {
  const theme = useContext(ThemeContext);
  const [iconType, setIconType] = useState('call');
  const [isRtiTooltipVisible, setIsRtiTooltipVisible] = useState(false);
  const {
    isPremiumUser,
    applicationType,
    classOfService,
    isAdmin,
    collaboratorType,
    pairedAccountName,
    isFord = false,
    isRtiDownstreamSharer = false,
    downstreamAgencies,
    callerID,
  } = props;

  const coloredAliEnabled = useBooleanFlagValue('colored-ali', false);
  const callCore = useSelector(
    state => state.emergencyCalls?.callQueue?.[callerID]?.core,
  );
  const showAliIcon = coloredAliEnabled && hasAliData(callCore);

  const resolveCallIcon = () => {
    const isAniAli = applicationType?.startsWith(ALI_UNDERSCORE);
    // Takes precedence over every other card treatment below.
    if (collaboratorType === RTI_DOWNSTREAM_SHARE) {
      return (
        <Box
          position="relative"
          onMouseEnter={() => setIsRtiTooltipVisible(true)}
          onMouseLeave={() => setIsRtiTooltipVisible(false)}
        >
          <ArrowForwardIcon
            width={16}
            height={12}
            color={theme.colors.lightWarning}
          />
          {isRtiTooltipVisible && (
            <Absolute
              top="calc(100% + 4px)"
              left={0}
              width={RTI_TOOLTIP_WIDTH}
              zIndex={10}
            >
              <RtiTooltipBubble>
                {getCollaboratorTypeDisplayName(
                  collaboratorType,
                  pairedAccountName,
                )}
              </RtiTooltipBubble>
            </Absolute>
          )}
        </Box>
      );
    }

    const sharedColor = isRtiDownstreamSharer
      ? theme.colors.lightWarning
      : undefined;
    const icon = (() => {
      const CarGlyph = showAliIcon ? AssociatedCarIcon : CarIcon;
      const SmartphoneGlyph = showAliIcon
        ? AssociatedSmartphoneIcon
        : SmartphoneIcon;
      const MarkerGlyph = showAliIcon ? AssociatedMarkerIcon : MarkerIcon;
      const SMSGlyph = showAliIcon ? AssociatedSMSIcon : SMSIcon;
      const VOIPGlyph = showAliIcon ? AssociatedVOIPIcon : VOIPIcon;
      const LandlineGlyph = showAliIcon ? AssociatedLandlineIcon : LandlineIcon;
      const PhoneGlyph = showAliIcon ? AssociatedPhoneIcon : PhoneIcon;

      if (isFord)
        return <CarGlyph width={24} height={24} color={sharedColor} />;
      if (isAdmin)
        return <SmartphoneGlyph width={24} height={16} color={sharedColor} />;
      if (collaboratorType === CALL_REROUTED)
        return <MarkerGlyph width={24} height={16} color={sharedColor} />;
      if (classOfService === TEXT)
        return <SMSGlyph width={24} height={16} color={sharedColor} />;
      if (
        isPremiumUser &&
        isAniAli &&
        ((classOfService && !classOfService?.startsWith(PHASE)) ||
          !classOfService)
      ) {
        if (classOfService === VOIP) {
          return (
            <VOIPGlyph
              width={24}
              height={16}
              viewboxWidth={16}
              viewboxHeight={16}
              color={sharedColor}
            />
          );
        }
        return <LandlineGlyph width={24} height={16} color={sharedColor} />;
      }
      if (isPremiumUser)
        return <SmartphoneGlyph width={24} height={16} color={sharedColor} />;
      return (
        <PhoneGlyph
          width={24}
          height={16}
          viewboxWidth={11}
          viewboxHeight={15}
          color={sharedColor}
        />
      );
    })();

    if (!isRtiDownstreamSharer && !showAliIcon) return icon;

    return (
      <Box
        position="relative"
        onMouseEnter={() => setIsRtiTooltipVisible(true)}
        onMouseLeave={() => setIsRtiTooltipVisible(false)}
      >
        {icon}
        {isRtiTooltipVisible && (
          <Absolute
            top="calc(100% + 4px)"
            left={0}
            width={RTI_TOOLTIP_WIDTH}
            zIndex={10}
          >
            <RtiTooltipBubble>
              {isRtiDownstreamSharer && (
                <div>{`Call shared with ${formatDownstreamAgencyNames(downstreamAgencies)}`}</div>
              )}
              {showAliIcon && <div>Call Verified</div>}
            </RtiTooltipBubble>
          </Absolute>
        )}
      </Box>
    );
  };

  const getIcons = type => {
    const iconUnseenColor = theme.colors.queue.emergencyCall.unseen;
    const icons = {
      call: resolveCallIcon(),
      callReceived: <CallReceivedIcon width={24} height={16} />,
      callReceivedUnseen: (
        <CallReceivedIcon width={24} height={16} color={iconUnseenColor} />
      ),
      callShared: <CallSharedIcon width={24} height={16} />,
      search: <MagnifyingGlassIcon width={24} height={16} />,
      sms: <SMSIcon width={24} height={16} />,
      smsReceived: <SMSReceivedIcon width={25} height={25} />,
      smsReceivedUnseen: (
        <SMSReceivedIcon width={25} height={25} color={iconUnseenColor} />
      ),
      smsShared: <SMSSharedIcon width={25} height={26} />,
    };

    return icons[type];
  };

  const checkIconType = status => {
    const {
      isManualSearch,
      isSMSReceived,
      isSMSShared,
      isSMS,
      isCallReceived,
      isCallShared,
      isUnseen,
    } = status;
    if (isManualSearch) return 'search';
    else if (isSMSReceived)
      return isUnseen ? 'smsReceivedUnseen' : 'smsReceived';
    else if (isSMSShared) return 'smsShared';
    else if (isCallReceived)
      return isUnseen ? 'callReceivedUnseen' : 'callReceived';
    else if (isCallShared) return 'callShared';
    else if (isSMS) return 'sms';
    else return 'call';
  };

  useEffect(() => {
    setIconType(checkIconType(props));
  }, [props]);

  return <>{getIcons(iconType)}</>;
};

EmergencyCallIcons.propTypes = {
  isManualSearch: PropTypes.bool,
  isCallReceived: PropTypes.bool,
  isCallShared: PropTypes.bool,
  isSMS: PropTypes.bool,
  isSMSReceived: PropTypes.bool,
  isSMSShared: PropTypes.bool,
  isUnseen: PropTypes.bool,
  isFord: PropTypes.bool,
  isAdmin: PropTypes.bool,
  isPremiumUser: PropTypes.bool,
  applicationType: PropTypes.string,
  classOfService: PropTypes.string,
  collaboratorType: PropTypes.string,
  pairedAccountName: PropTypes.string,
  isRtiDownstreamSharer: PropTypes.bool,
  callerID: PropTypes.string,
  downstreamAgencies: PropTypes.arrayOf(
    PropTypes.shape({
      accountID: PropTypes.string,
      displayName: PropTypes.string,
    }),
  ),
};

export default memo(EmergencyCallIcons);
